import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import { graphql } from "gatsby";
import { GatsbyImage } from "gatsby-plugin-image";
import React, { DetailedHTMLProps, FC, HTMLAttributes, useState } from "react";

import { CommonColors } from "../../../types/colors";
import { getTailwindLineClamp } from "../../../utils/tailwind/get-tailwind-line-clamp";
import { Cta } from "../../buttons/cta";
import { Highlights } from "../../highlights";
import { PaginationIndicator } from "../../pagination/pagination-indicator";
import { PaginationIndicatorColor } from "../../pagination/pagination-indicator/types";
import { colorScheme as colorSchemeClasses } from "./styles";
import { HorizontalCardColorVariants, HorizontalCardTitleSize } from "./types";

export interface HorizontalCardProps
  extends Queries.HorizontalCardDataFragment,
    Pick<DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>, "className"> {
  showBody?: boolean;
  showHighlights?: boolean;
  showMultipleImages?: boolean;
  lineClamp?: number;
}

export const HorizontalCard: FC<HorizontalCardProps> = ({
  className,
  colorScheme: _colorScheme,
  titleSize,
  images,
  title,
  description,
  highlights,
  ctas,
  showBody = true,
  showHighlights = true,
  showMultipleImages = true,
  lineClamp = 0,
}) => {
  const [page, setPage] = useState(0);
  const colorScheme = (_colorScheme ??
    HorizontalCardColorVariants.Default) as HorizontalCardColorVariants;
  const selectedImage = images?.[page];
  const hasBody = title || description || ctas;
  const { titleClass, rootClass, descriptionClass } = colorSchemeClasses[colorScheme];
  return (
    <div
      className={clsx([
        "flex flex-col lg:flex-row items-start",
        "rounded-3xl min-h-80 overflow-hidden",
        "transition-all duration-500",
        rootClass,
        className,
      ])}
    >
      <div
        className={clsx(
          "relative lg:h-auto w-full lg:self-stretch overflow-hidden flex-[1_1_33%]",
          hasBody ? "lg:flex-[1_0_50%]" : "flex-none"
        )}
      >
        {selectedImage && (
          <AnimatePresence initial={false} mode="popLayout">
            <motion.div
              className="w-full h-full absolute"
              key={page}
              initial={{
                x: "100%",
              }}
              animate={{
                x: "0%",
              }}
              exit={{
                x: "-100%",
              }}
              transition={{
                x: { ease: "easeOut", duration: 0.45 },
              }}
            >
              <GatsbyImage
                image={selectedImage?.gatsbyImageData!}
                alt={title ?? ""}
                objectFit="cover"
                className={clsx("w-full h-full pointer-events-none")}
              />
            </motion.div>
          </AnimatePresence>
        )}

        {images && showMultipleImages && images.length > 1 && (
          <PaginationIndicator
            activeItem={page}
            color={PaginationIndicatorColor.Light}
            totalItems={images.length}
            onClickItem={(i) => setPage(i)}
            className="z-10 absolute bottom-5 px-5 w-full"
          />
        )}
      </div>
      {hasBody && (
        <div
          className={clsx([
            "w-full lg:w-1/2",
            "flex flex-col items-start gap-4 lg:gap-5",
            "pt-4 pb-10 lg:py-14 px-7 xl:px-10 2xl:px-20",
          ])}
        >
          {title && (
            <div
              className={clsx([
                "leading-none break-normal",
                titleSize === HorizontalCardTitleSize.Small
                  ? "text-lg lg:text-2xl"
                  : titleSize === HorizontalCardTitleSize.Large
                  ? "text-7xl lg:text-8xl"
                  : "text-2xl lg:text-4xl",
                titleClass,
              ])}
            >
              {title}
            </div>
          )}
          <AnimatePresence initial={false}>
            {showBody && (
              <motion.div
                className="flex flex-col items-start gap-4 lg:gap-5 w-full overflow-hidden"
                initial={{
                  height: "0px",
                }}
                animate={{
                  height: "auto",
                }}
                exit={{
                  height: "0px",
                  transition: { ease: "easeOut", duration: 0.6 },
                }}
                transition={{
                  height: { ease: "easeOut", duration: 0.2 },
                }}
              >
                {description && (
                  <div
                    className={clsx(
                      "text-base lg:text-lg leading-snug font-light line-clamp-[8] break-normal",
                      descriptionClass,
                      getTailwindLineClamp(lineClamp)
                    )}
                  >
                    {description.text}
                  </div>
                )}
                {showHighlights && highlights && (
                  <Highlights
                    {...highlights}
                    maxVisibleItems={1}
                    color={CommonColors.Light}
                    className="w-full"
                  />
                )}
                {ctas?.length &&
                  ctas
                    .filter((cta) => !!cta?.label)
                    .map((cta, index) => (
                      <Cta
                        key={index}
                        variant={
                          _colorScheme === HorizontalCardColorVariants.PrimaryBackground
                            ? "primary-inverted"
                            : "primary"
                        }
                        {...cta!}
                        buttonClassName="!whitespace-normal py-4 !h-auto"
                      />
                    ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

export const query = graphql`
  fragment HorizontalCardData on ContentfulHorizontalCard {
    __typename
    colorScheme
    titleSize
    title
    description {
      text: description
    }
    images {
      gatsbyImageData
    }
    highlights {
      ...HighlightsData
    }
    ctas {
      ...CtaData
    }
  }
`;
